import { useState, useEffect } from 'react';
import { useTypedQueryParams, UseTypedQueryParamsResult } from '../../hooks/useTypedQueryParams';
import { z } from 'zod';
import { ActionStatus } from '../../gql/graphql';
import { IfEquals } from '../../types/IfEquals';
import { prioritiesOptions } from '../../form/PriorityCheckList';

export const actionStatusOptions = ['approved', 'open', 'rejected', 'resolved'] as const;

// We confirm that the Status options exactly match the GQL union type: no value is extra or missing
// An error will cause the type-check build step to fail.
true satisfies IfEquals<ActionStatus, (typeof actionStatusOptions)[number], true, false>;

const filtersQueryParamsSchema = z.object({
	filters: z
		.object({
			journeyId: z.string().optional(),
			date: z
				.object({
					start: z.string().datetime({
						offset: true,
					}),
					end: z.string().datetime({
						offset: true,
					}),
				})
				.optional(),
			deadline: z
				.object({
					start: z.string().datetime({
						offset: true,
					}),
					end: z.string().datetime({
						offset: true,
					}),
				})
				.optional(),
			tags: z.array(z.string()).optional(),
			owners: z.array(z.string()).optional(),
			status: z.array(z.enum(actionStatusOptions)).optional(),
			priority: z.array(z.enum(prioritiesOptions)).optional(),
		})
		.optional(),
});

type ActionFilters = NonNullable<z.infer<typeof filtersQueryParamsSchema>['filters']>;

const useActionFilters = (): UseTypedQueryParamsResult<ActionFilters> => {
	const [params, setParams] = useTypedQueryParams(filtersQueryParamsSchema);
	const [isTouched, setIsTouched] = useState(false);

	const defaultFilters: ActionFilters = {
		status: ['open', 'approved'],
	};

	const filters = isTouched ? (params.filters ?? {}) : { ...defaultFilters, ...params.filters };

	const setFilters = (filters: ActionFilters) => {
		setIsTouched(true);
		setParams({ filters });
	};

	useEffect(() => {
		if (params.filters) {
			setIsTouched(true);
		}
	}, [params.filters]);

	return [filters, setFilters];
};

export { useActionFilters, type ActionFilters };
